// API Configuration for Polymath Kids Frontend
// This file centralizes all API endpoint configurations

const API_CONFIG = {
  // Base API URL - this should match your backend server
  BASE_URL: (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API) || 'http://localhost:5000',

  // Backend server URL (without /api)
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001',

  // Images base URL
  IMAGES_URL: (process.env.REACT_APP_IMAGES_URL && process.env.REACT_APP_IMAGES_URL !== 'undefined') ? process.env.REACT_APP_IMAGES_URL : 'http://localhost:5001/public',

  // API Endpoints
  ENDPOINTS: {
    // Authentication
    LOGIN: '/login',
    SIGNUP: '/register',
    LOGOUT: '/logout',
    USER: '/user',
    CHECK_UNIQUE_USERNAME: '/checkUniqueUsername',
    REGISTER_IN_STUDENT_TABLE: '/registerInStudentTable',

    // Contact Form
    CONTACT: '/contact',

    // Newsletter Subscription
    ADD_NEWSLETTER_EMAIL: '/addNewsletterEmail',

    // Student Management
    GET_STUDENT_ID: '/getStudentId',
    GET_STUDENT_REWARDS: '/getStudentRewards',
    GET_STUDENT_INVOICES: '/studentInvoices',
    GET_STUDENT_INVOICE_DETAILS: '/studentInvoiceDetails',
    MARK_PAYMENT_DONE: '/markPaymentDone',
    AMOUNT_PAID: '/amountPaid',

    // Course Management
    GET_MY_COURSES: '/getmyCourses',
    GET_TEACHER_TAUGHT_COURSES: '/getTeacherTaughtCourses',
    FETCH_STUDENT_REGISTERED_COURSES: '/fetchStudentRegisteredCourses',
    GET_TEACHER_IDS_VIA_CLASS_ID: '/getTeacherIdsViaClassId',

    // User Management
    GET_USERS: '/getUsers',
    SET_TABLES: '/setTables',
    UPDATE_TYPE: '/updateType',
    UPDATE_USER_STATUS: '/updateUserStatus',
    GET_COURSENAMES_BY_USER_ID: '/getCoursenamesByUserId',
    GET_CURRENT_STATUS: '/getCurrentStatus',

    // Course Details
    GET_COURSE_NAME: '/getCourseName',
    GET_MODULES_BY_COURSE: '/getModulesByCourse',
    GET_MODULE_NAME: '/getModuleName',
    GET_LESSONS_BY_MODULE: '/getLessonsByModule',
    GET_LESSON_NAME: '/getLessonName',

    // Activities and Projects
    GET_STUDENT_ID_FROM_USER: '/getStudentIDfromUser',
    GET_ACTIVITY_ID_FROM_STUDENT: '/getActivityIDfromStudent',
    GET_COURSE_ID_FROM_ACTIVITY_ID: '/getCourseIDfromActivityID',
    GET_STUDENT_MODULE_PROJECT_ASSIGNMENT: '/getStudentModuleProjectAssignment',
    GET_STUDENT_LESSON_PROJECT_ASSIGNMENT: '/getStudentLessonProjectAssignment',
    GET_STUDENT_LESSON_ACTIVITY: '/getStudentlessonactivity',
    UPLOAD_STUDENT_LESSON_PROJECT: '/uploadStudentLessonProject',

    // Rewards System
    GET_REWARDS_FOR_STUDENTS: '/getRewardsForStudents',
    STUDENT_REDEEM_HISTORY: '/StudentRedeemHistory',
    REDEEM_GIFT: '/redeemGift',

    // Fees and Payments
    BANK_DETAILS: '/bankDetails',
    GET_DISCOUNTS: '/getDiscounts',
    GENERATE_INVOICE_NUMBER: '/generateInvoiceNumber',
    GENERATE_INVOICE: '/generateInvoice',
    ALL_STUDENTS_INVOICE_DETAILS: '/allStudentsInvoiceDetails',

    // Email and Communication
    GET_EMAIL_BY_USERNAME: '/getEmailByUsername',
    SEND_EMAIL: '/sendEmail',
    EMAIL_STATUS: '/emailStatus',
    SEND_MASS_EMAIL: '/sendMassEmail',

    // Polymatch Competition
    POLYMATCH_COMPETITION: '/polymatchcompetition',
    ALL_POLYMATCH_COMPETITIONS: '/allpolymatchcompetition',
    POLYMATCH_REGISTER: '/polymatchregister',
    POLYMATCH_RESULTS: '/polymatch_results',
    STUDENTS_RESULTS_ENG: '/students_results_eng',
    STUDENTS_RESULTS_ISL: '/students_results_Isl',

    // File Management
    GET_INVOICE_FILE: '/getInvoiceFile',
    GET_UIMAGE: '/getUimage',

    // Lesson Activity Profile
    GET_LESSON_ACTIVITY_PROFILE_DATA: '/getLessonActivityProfileData',

    // Data Display - New endpoints
    GET_ALL_DATA: '/api/data/getAllData',
    GET_TABLE_COLUMNS: '/api/data/getTableColumns',
    GET_TABLES: '/api/data/getTables',
  },

  // Request Configuration
  REQUEST_CONFIG: {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  },

  // File Upload Configuration
  UPLOAD_CONFIG: {
    credentials: 'include',
  }
};

/*
 * Walkthrough Summary:
 * - [x] Create centralized configuration in `src/config/api.js`
 * - [x] Update core frontend components (`App.js`, `Home.js`, `OfferingItem.js`, `GallerySection.js`, `FeaturedBlogs.js`)
 * - [x] Update testimonial and card components (`TestimonialCard.js`, `WinnerCard.js`, `AdvantageItem.js`)
 * - [x] Update functional sections (`FunActivities.js`, `PopularCourses.js`, `Benefits.js`, `Highlights.js`)
 * - [/] Update all remaining promotional and portal pages
 *     - [x] `Blogs.js`, `Blog1.js`, `Blog2.js`, `Blog3.js`
 *     - [x] `Gallery.js`
 *     - [x] `WinterCamp.js`, `SummerCamp.js`, `SumCourses.js`, `LifeSkillCourses.js`
 *     - [ ] STEM Portals (Math, Coding, Engineering, etc.)
 * - [ ] Final verification of all images across the application
 * - [ ] Clean up unused assets and debug logs
 */

// Helper function to build full API URL
export const buildApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to build backend URL (without /api)
export const buildBackendUrl = (endpoint) => {
  return `${API_CONFIG.BACKEND_URL}${endpoint}`;
};

// Helper function to get request config with credentials
export const getRequestConfig = (customConfig = {}) => {
  return {
    ...API_CONFIG.REQUEST_CONFIG,
    ...customConfig,
  };
};

// Helper function to get upload config
export const getUploadConfig = (customConfig = {}) => {
  return {
    ...API_CONFIG.UPLOAD_CONFIG,
    ...customConfig,
  };
};

export default API_CONFIG; 